import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Search } from "lucide-react";
import { BLOG_POSTS } from "../lib/data";
import PageHero from "../components/PageHero";
import CTASection from "../components/CTASection";
import { staggerChild } from "../lib/motion";

export default function Blog() {
  const categories = useMemo(
    () => ["All", ...Array.from(new Set(BLOG_POSTS.map((p) => p.category)))],
    []
  );
  const [active, setActive] = useState("All");
  const [query, setQuery] = useState("");

  const filtered = BLOG_POSTS.filter((p) => {
    const matchCat = active === "All" || p.category === active;
    const q = query.trim().toLowerCase();
    const matchQ = !q || p.title.toLowerCase().includes(q) || p.excerpt.toLowerCase().includes(q);
    return matchCat && matchQ;
  });

  const [featured, ...rest] = filtered;

  return (
    <>
      <PageHero eyebrow="Journal" title="Ideas, playbooks, opinions." subtitle="A thoughtfully-edited journal about modern websites, AI and small-business growth. No filler." />

      <section className="pb-24" data-testid="blog-section">
        <div className="container-page">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setActive(c)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                    active === c
                      ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white"
                      : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-800 hover:border-blue-400"
                  }`}
                  data-testid={`blog-filter-${c.toLowerCase()}`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="relative w-full md:w-72">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search articles"
                className="w-full rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 pl-11 pr-4 py-2.5 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100"
                data-testid="blog-search"
              />
            </div>
          </div>

          {featured && (
            <motion.div {...staggerChild} className="mb-16">
              <Link to={`/blog/${featured.slug}`} className="group grid lg:grid-cols-2 gap-10 items-center" data-testid={`blog-featured-${featured.slug}`}>
                <div className="aspect-[16/11] rounded-[24px] overflow-hidden border border-slate-100 dark:border-slate-800">
                  <img src={featured.cover} alt={featured.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                </div>
                <div>
                  <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
                    <span className="badge-pill">{featured.category}</span>
                    <span>{new Date(featured.date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</span>
                    <span>•</span>
                    <span>{featured.read}</span>
                  </div>
                  <h2 className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tighter text-slate-900 dark:text-white group-hover:text-blue-600 transition-colors leading-tight">{featured.title}</h2>
                  <p className="mt-4 text-lg text-slate-600 dark:text-slate-300 leading-relaxed">{featured.excerpt}</p>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 dark:text-blue-400 group-hover:gap-2 transition-all">
                    Read article <ArrowUpRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            </motion.div>
          )}

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {rest.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group block" data-testid={`blog-post-${p.slug}`}>
                <div className="aspect-[4/3] rounded-[20px] overflow-hidden border border-slate-100 dark:border-slate-800">
                  <img src={p.cover} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                </div>
                <div className="mt-5 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                  <span className="badge-pill">{p.category}</span>
                  <span>{p.read}</span>
                </div>
                <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white group-hover:text-blue-600 transition-colors leading-snug">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{p.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection eyebrow="Enjoying the read?" title="Let's talk about your project." />
    </>
  );
}
